import { useMemo, useRef, useState } from 'react'
import { FileText, Target, Sparkles, Mail, Link2, Globe, RefreshCcw, CheckCircle2, AlertCircle, X } from 'lucide-react'
import { useLocale } from '../locales/useLocale'
import { fetchWithRetry, getApiHeaders } from '../utils/api'

const MODE_LABELS = {
  'zh-TW': {
    url: '分析網址',
    manual: '貼上內容',
    urlLabel: '頁面網址',
    urlPlaceholder: 'https://example.com/article',
    fetch: '擷取網址內容',
    refetch: '重新擷取',
    fetchedTitle: '已擷取來源頁面',
    sourceUrl: '分析來源網址',
    finalUrl: '最終轉址網址',
    fetchStatus: '抓取狀態',
    fetchReady: '已完成擷取，可直接開始分析。',
    fetchHint: '先擷取網址內容，再送出分析。',
    fetchEmpty: '請輸入網址',
    fetchPreview: '內容預覽',
    fetchChars: '預覽字數',
    fetchErrorPrefix: '擷取失敗',
    previewStable: '目前顯示的是分析實際使用的正文片段。',
    previewUnstable: '這次擷取可能退回整頁 body，預覽未必是純正文，分析結果可信度也會受影響。'
  },
  en: {
    url: 'Analyze URL',
    manual: 'Paste Content',
    urlLabel: 'Page URL',
    urlPlaceholder: 'https://example.com/article',
    fetch: 'Fetch Page Content',
    refetch: 'Fetch Again',
    fetchedTitle: 'Fetched Page Snapshot',
    sourceUrl: 'Source URL',
    finalUrl: 'Final URL',
    fetchStatus: 'Fetch Status',
    fetchReady: 'Page content fetched and ready for analysis.',
    fetchHint: 'Fetch the page first, then run the analysis.',
    fetchEmpty: 'Please enter a URL.',
    fetchPreview: 'Preview',
    fetchChars: 'Preview chars',
    fetchErrorPrefix: 'Fetch failed',
    previewStable: 'This preview matches the body used for analysis.',
    previewUnstable: 'This fetch likely fell back to the full body, so the preview may not be clean article content.'
  },
  ja: {
    url: 'URL 分析',
    manual: '内容を貼り付け',
    urlLabel: 'ページ URL',
    urlPlaceholder: 'https://example.com/article',
    fetch: 'ページ内容を取得',
    refetch: '再取得',
    fetchedTitle: '取得済みページ',
    sourceUrl: '分析元 URL',
    finalUrl: '最終到達 URL',
    fetchStatus: '取得状態',
    fetchReady: 'ページ内容の取得が完了し、分析できます。',
    fetchHint: '先に URL を取得してから分析を実行してください。',
    fetchEmpty: 'URL を入力してください。',
    fetchPreview: 'プレビュー',
    fetchChars: 'プレビュー文字数',
    fetchErrorPrefix: '取得失敗',
    previewStable: 'このプレビューは分析に使う本文と一致します。',
    previewUnstable: '今回は本文抽出が不安定で、ページ全体にフォールバックしている可能性があります。'
  }
}

function getModeStrings(locale) {
  return MODE_LABELS[locale] || MODE_LABELS['zh-TW']
}

export default function InputSection({ onAnalyze, isLoading, apiBaseUrl, sessionId }) {
  const editorRef = useRef(null)
  const keywordInputRef = useRef(null)
  const [inputMode, setInputMode] = useState('url')
  const [deliveryMode, setDeliveryMode] = useState('sync')
  const [contentPlain, setContentPlain] = useState('')
  const [contentHtml, setContentHtml] = useState('')
  const [contentMarkdown, setContentMarkdown] = useState('')
  const [targetKeywords, setTargetKeywords] = useState([])
  const [targetKeywordsInput, setTargetKeywordsInput] = useState('')
  const [isKeywordComposing, setIsKeywordComposing] = useState(false)
  const [email, setEmail] = useState('')
  const [contentUrl, setContentUrl] = useState('')
  const [fetchError, setFetchError] = useState('')
  const [fetchingUrl, setFetchingUrl] = useState(false)
  const [fetchedContent, setFetchedContent] = useState(null)
  const [error, setError] = useState('')
  const { strings, locale } = useLocale()
  const { input } = strings
  const modeStrings = getModeStrings(locale)

  const handleEditorInput = (event) => {
    const html = event.currentTarget.innerHTML
    const text = event.currentTarget.innerText || ''
    setContentHtml(html)
    setContentPlain(text)

    if (!text.trim()) {
      setContentMarkdown('')
    }
  }

  const handleEditorPaste = (event) => {
    const clipboard = event.clipboardData
    if (!clipboard) return

    const markdown = clipboard.getData('text/markdown')
    setContentMarkdown(markdown || '')

    const html = clipboard.getData('text/html')
    if (!html) {
      return
    }

    event.preventDefault()

    const editor = editorRef.current
    if (!editor) {
      return
    }

    editor.focus()

    const selection = typeof window !== 'undefined' ? window.getSelection() : null
    if (!selection) {
      return
    }

    if (!selection.rangeCount || !editor.contains(selection.anchorNode)) {
      const range = document.createRange()
      range.selectNodeContents(editor)
      range.collapse(false)
      selection.removeAllRanges()
      selection.addRange(range)
    }

    if (
      typeof document !== 'undefined' &&
      typeof document.execCommand === 'function' &&
      (!document.queryCommandSupported || document.queryCommandSupported('insertHTML'))
    ) {
      document.execCommand('insertHTML', false, html)
    } else if (selection.rangeCount) {
      const range = selection.getRangeAt(0)
      range.deleteContents()
      const fragment = range.createContextualFragment(html)
      range.insertNode(fragment)
      range.collapse(false)
      selection.removeAllRanges()
      selection.addRange(range)
    }

    const updatedHtml = editor.innerHTML
    const updatedText = editor.innerText || ''

    setContentHtml(updatedHtml)
    setContentPlain(updatedText)

    if (!updatedText.trim()) {
      setContentMarkdown('')
    }
  }

  const handleUrlChange = (value) => {
    setContentUrl(value)
    setFetchError('')
    setError('')
    if (fetchedContent?.sourceUrl !== value.trim()) {
      setFetchedContent(null)
    }
  }

  const handleFetchUrl = async () => {
    if (!contentUrl.trim()) {
      setFetchError(modeStrings.fetchEmpty)
      return
    }

    setFetchingUrl(true)
    setFetchError('')
    setFetchedContent(null)

    try {
      const response = await fetchWithRetry(
        `${apiBaseUrl}/api/fetch-content`,
        {
          method: 'POST',
          headers: getApiHeaders(),
          body: JSON.stringify({
            contentUrl: contentUrl.trim(),
            sessionId
          })
        },
        1,
        800
      )
      const data = await response.json()
      setFetchedContent({
        plain: data?.plain || '',
        html: data?.html || '',
        markdown: data?.markdown || '',
        sourceUrl: contentUrl.trim(),
        finalUrl: data?.finalUrl || contentUrl.trim(),
        fetchMeta: data?.fetchMeta || null
      })
    } catch (err) {
      setFetchError(`${modeStrings.fetchErrorPrefix}：${err.message}`)
    } finally {
      setFetchingUrl(false)
    }
  }

  const normalizeKeywordCandidates = (values) => {
    return values
      .flatMap((value) => String(value || '').split(/[,，、\n\r]+/))
      .map((keyword) => keyword.trim().replace(/\s+/g, ' '))
      .filter(Boolean)
  }

  const addKeywordCandidates = (values) => {
    const candidates = normalizeKeywordCandidates(values)
    if (!candidates.length) return true

    let nextKeywords = [...targetKeywords]
    for (const candidate of candidates) {
      const alreadyExists = nextKeywords.some(
        (keyword) => keyword.toLocaleLowerCase() === candidate.toLocaleLowerCase()
      )
      if (!alreadyExists) {
        nextKeywords.push(candidate)
      }
    }

    if (nextKeywords.length > 5) {
      setError(input.errorKeywordsMax)
      return false
    }

    setTargetKeywords(nextKeywords)
    setTargetKeywordsInput('')
    setError('')
    return true
  }

  const removeKeyword = (keywordToRemove) => {
    setTargetKeywords((keywords) => keywords.filter((keyword) => keyword !== keywordToRemove))
    setError('')
  }

  const handleKeywordInputChange = (event) => {
    const value = event.target.value
    if (!isKeywordComposing && /[,，、\n\r]/.test(value)) {
      const parts = value.split(/[,，、\n\r]+/)
      const trailingPart = parts.pop() || ''
      addKeywordCandidates(parts)
      setTargetKeywordsInput(trailingPart)
      return
    }

    setTargetKeywordsInput(value)
    setError('')
  }

  const handleKeywordKeyDown = (event) => {
    if (event.nativeEvent?.isComposing || isKeywordComposing) {
      return
    }

    if (event.key === 'Enter' || event.key === 'Tab') {
      if (targetKeywordsInput.trim()) {
        event.preventDefault()
        addKeywordCandidates([targetKeywordsInput])
      }
      return
    }

    if (event.key === 'Backspace' && !targetKeywordsInput && targetKeywords.length > 0) {
      event.preventDefault()
      setTargetKeywords((keywords) => keywords.slice(0, -1))
    }
  }

  const handleKeywordPaste = (event) => {
    const pastedText = event.clipboardData?.getData('text')
    if (!pastedText || !/[,，、\n\r]/.test(pastedText)) return

    event.preventDefault()
    addKeywordCandidates([targetKeywordsInput, pastedText])
  }

  const getSubmittedKeywords = () => {
    return normalizeKeywordCandidates([...targetKeywords, targetKeywordsInput])
      .filter((keyword, index, keywords) => (
        keywords.findIndex((item) => item.toLocaleLowerCase() === keyword.toLocaleLowerCase()) === index
      ))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (isKeywordComposing) {
      return
    }

    const keywords = getSubmittedKeywords()

    if (keywords.length === 0) {
      setError(input.errorKeywordsRequired)
      return
    }
    if (keywords.length > 5) {
      setError(input.errorKeywordsMax)
      return
    }

    const requiresEmail = deliveryMode === 'async'
    if (requiresEmail && !email.trim()) {
      setError(input.errorInvalidEmail)
      return
    }

    if (requiresEmail && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError(input.errorInvalidEmail)
      return
    }

    if (inputMode === 'url') {
      if (!fetchedContent?.plain?.trim()) {
        setError(modeStrings.fetchHint)
        return
      }

      setError('')
      const payload = {
        plain: fetchedContent.plain,
        html: fetchedContent.html,
        markdown: fetchedContent.markdown,
        contentUrl: fetchedContent.sourceUrl,
        fetchedUrl: fetchedContent.finalUrl,
        fetchMeta: fetchedContent.fetchMeta
      }
      if (requiresEmail) {
        payload.email = email.trim()
      }

      onAnalyze(payload, keywords, {
        mode: 'url',
        locale,
        inputMode: 'url',
        sourceUrl: fetchedContent.sourceUrl,
        finalUrl: fetchedContent.finalUrl,
        fetchMeta: fetchedContent.fetchMeta
      })
      return
    }

    if (!contentPlain.trim()) {
      setError(input.errorEmptyContent)
      return
    }

    setError('')
    const payload = {
      plain: contentPlain,
      html: contentHtml,
      markdown: contentMarkdown
    }
    if (requiresEmail) {
      payload.email = email.trim()
    }

    onAnalyze(payload, keywords, {
      mode: 'manual',
      locale,
      inputMode: 'manual'
    })
  }

  const wordCount = useMemo(
    () => contentPlain.trim().replace(/\s/g, '').length,
    [contentPlain]
  )

  const fetchedPreviewLength = useMemo(
    () => (fetchedContent?.plain || '').trim().slice(0, 480).length,
    [fetchedContent]
  )

  return (
    <div className="card">
      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <div className="inline-flex rounded-xl bg-gray-100 p-1">
            {[
              { id: 'url', label: modeStrings.url, icon: Globe },
              { id: 'manual', label: modeStrings.manual, icon: FileText }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setInputMode(id)
                  setError('')
                }}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  inputMode === id
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {inputMode === 'url' ? (
          <div className="space-y-5 mb-6">
            <div>
              <label className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-3">
                <Link2 className="w-5 h-5 text-primary-600" />
                {modeStrings.urlLabel}
              </label>
              <div className="flex flex-col gap-3 md:flex-row">
                <input
                  type="url"
                  value={contentUrl}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  placeholder={modeStrings.urlPlaceholder}
                  className="input-field flex-1"
                  disabled={isLoading || fetchingUrl}
                  required={inputMode === 'url'}
                />
                <button
                  type="button"
                  onClick={handleFetchUrl}
                  disabled={isLoading || fetchingUrl || !contentUrl.trim()}
                  className="btn-primary inline-flex items-center justify-center gap-2 md:w-[180px]"
                >
                  {fetchingUrl ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                  {fetchedContent ? modeStrings.refetch : modeStrings.fetch}
                </button>
              </div>
              {fetchError && (
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{fetchError}</span>
                </div>
              )}
            </div>

            {fetchedContent && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-3">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="w-5 h-5" />
                  <h3 className="text-sm font-semibold">{modeStrings.fetchedTitle}</h3>
                </div>
                <div className="grid gap-2 text-sm text-gray-700">
                  <div><span className="font-semibold text-gray-900">{modeStrings.sourceUrl}：</span>{fetchedContent.sourceUrl}</div>
                  <div><span className="font-semibold text-gray-900">{modeStrings.finalUrl}：</span>{fetchedContent.finalUrl}</div>
                  <div><span className="font-semibold text-gray-900">{modeStrings.fetchStatus}：</span>{fetchedContent.fetchMeta?.status || 'ok'}</div>
                  <div><span className="font-semibold text-gray-900">{modeStrings.fetchChars}：</span>{fetchedPreviewLength}</div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1">{modeStrings.fetchPreview}</p>
                  <div className="rounded-lg border border-emerald-100 bg-white p-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
                    {(fetchedContent.plain || '').trim().slice(0, 480)}
                  </div>
                </div>
                <p className={`text-xs ${fetchedContent.fetchMeta?.extractionStable === false ? 'text-amber-700' : 'text-emerald-700'}`}>
                  {fetchedContent.fetchMeta?.extractionStable === false ? modeStrings.previewUnstable : modeStrings.previewStable}
                </p>
                <p className="text-xs text-emerald-700">{modeStrings.fetchReady}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="mb-6">
            <label className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-3">
              <FileText className="w-5 h-5 text-primary-600" />
              {input.contentLabel}
            </label>
            <div className="relative">
              <div
                ref={editorRef}
                className={`input-field min-h-[300px] resize-y font-mono text-sm whitespace-pre-wrap ${
                  error ? 'border-red-500' : ''
                } ${isLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                contentEditable={!isLoading}
                suppressContentEditableWarning
                onInput={handleEditorInput}
                onPaste={handleEditorPaste}
                aria-label={input.contentLabel}
              />
              {!contentPlain.trim() && (
                <span className="pointer-events-none absolute left-3 top-3 text-sm text-gray-400">
                  {input.contentPlaceholder}
                </span>
              )}
            </div>
            <div className="flex justify-between mt-2">
              <div className="text-sm text-gray-500">
                {input.wordCountLabel}: {wordCount} {input.wordCountUnit}
              </div>
            </div>
          </div>
        )}

        <div className="mb-6">
          <label className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-3">
            <Target className="w-5 h-5 text-primary-600" />
            {input.keywordsLabel}{' '}
            <span className="text-sm font-normal text-gray-500">{input.keywordsHint}</span>
          </label>
          <div
            className={`input-field flex min-h-[52px] flex-wrap items-center gap-2 ${
              isLoading || fetchingUrl ? 'bg-gray-100 cursor-not-allowed' : ''
            }`}
            onClick={() => {
              if (!isLoading && !fetchingUrl) {
                keywordInputRef.current?.focus()
              }
            }}
          >
            {targetKeywords.map((keyword) => (
              <span
                key={keyword}
                className="inline-flex max-w-full items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-sm font-medium text-primary-800"
              >
                <span className="max-w-[220px] truncate">{keyword}</span>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    removeKeyword(keyword)
                  }}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full text-primary-700 hover:bg-primary-100"
                  disabled={isLoading || fetchingUrl}
                  aria-label={`${input.keywordsLabel}: ${keyword}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
            <input
              id="target-keywords-input"
              ref={keywordInputRef}
              type="text"
              value={targetKeywordsInput}
              onChange={handleKeywordInputChange}
              onKeyDown={handleKeywordKeyDown}
              onPaste={handleKeywordPaste}
              onCompositionStart={() => setIsKeywordComposing(true)}
              onCompositionEnd={(event) => {
                setIsKeywordComposing(false)
                setTargetKeywordsInput(event.currentTarget.value)
              }}
              placeholder={targetKeywords.length ? '' : input.keywordsPlaceholder}
              className="min-w-[160px] flex-1 border-none bg-transparent p-0 text-base outline-none placeholder:text-gray-400 disabled:cursor-not-allowed"
              disabled={isLoading || fetchingUrl}
              aria-label={input.keywordsLabel}
            />
          </div>
        </div>

        <div className="mb-6 rounded-2xl border border-primary-100 bg-primary-50/50 p-4">
          <div className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-3">
            <Mail className="w-5 h-5 text-primary-600" />
            {input.deliveryModeLabel}
          </div>
          <p className="text-sm text-gray-600 mb-4">{input.deliveryModeHint}</p>
          <div className="grid gap-3 md:grid-cols-2">
            {[
              { id: 'sync', title: input.deliverySyncLabel, description: input.submitSync },
              { id: 'async', title: input.deliveryAsyncLabel, description: input.deliveryAsyncDescription }
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  setDeliveryMode(option.id)
                  setError('')
                }}
                className={`rounded-xl border px-4 py-4 text-left transition ${
                  deliveryMode === option.id
                    ? 'border-primary-400 bg-white shadow-sm'
                    : 'border-gray-200 bg-white/70 hover:border-primary-200'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-gray-900">{option.title}</span>
                  <span
                    className={`inline-flex h-5 w-5 items-center justify-center rounded-full border text-xs ${
                      deliveryMode === option.id
                        ? 'border-primary-500 bg-primary-600 text-white'
                        : 'border-gray-300 text-transparent'
                    }`}
                  >
                    ✓
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-600">{option.description}</p>
              </button>
            ))}
          </div>
        </div>

        {deliveryMode === 'async' && (
          <div className="mb-6">
            <label className="flex items-center gap-2 text-lg font-semibold text-gray-700 mb-3">
              <Mail className="w-5 h-5 text-primary-600" />
              {input.emailLabel}{' '}
              <span className="text-sm font-normal text-gray-500">{input.emailRequiredHint}</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={input.emailPlaceholder}
              className="input-field"
              disabled={isLoading || fetchingUrl}
              required={deliveryMode === 'async'}
            />
            <p className="text-xs text-gray-500 mt-2">{input.emailOptionalHint}</p>
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={
            isLoading ||
            fetchingUrl ||
            (inputMode === 'manual' ? !contentPlain.trim() : !fetchedContent?.plain?.trim())
          }
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          <Sparkles className="w-5 h-5" />
          {isLoading
            ? input.submitLoading
            : deliveryMode === 'async'
              ? input.submitAsync
              : input.submitSync}
        </button>
      </form>
    </div>
  )
}
